/**
 * @fileoverview Syndicator Showdown — Complete Game Setup Wizard Dashboard
 * @module pages/admin/AdminSetupPage
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  doc, getDoc, getDocs, collection, query, where, orderBy, limit,
  serverTimestamp, writeBatch, updateDoc, Timestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../services/firebase';
import { fetchAvailableRaces } from '../../services/adminFirestore';
import { getCountdownTo } from '../../utils/timezones';
import { linkMeetingToShowdown } from '../../services/upcomingMeetingsService';
import './AdminSetupPage.css';

// ─── Shared Constants & Configuration Templates ──────────────────────────────────

const DEFAULT_PRIZE_POOL = { first: 500000, second: 200000, third: 100000, fourth: 60000, fifth: 40000 };
const PRIZE_RATIOS = { second: 0.40, third: 0.20, fourth: 0.12, fifth: 0.08 };
const POSITION_LABELS = { first: '1st', second: '2nd', third: '3rd', fourth: '4th', fifth: '5th' };

const PRIZE_PRESETS = {
  'Maiden':     { first: 25000,   second: 8000,   third: 4000,   fourth: 2500,  fifth: 1500  },
  'Country':    { first: 50000,   second: 16000,  third: 8000,   fourth: 5000,  fifth: 3000  },
  'Provincial': { first: 100000,  second: 32000,  third: 16000,  fourth: 10000, fifth: 6000  },
  'Metro':      { first: 200000,  second: 65000,  third: 32000,  fourth: 20000, fifth: 12000 },
  'Listed':     { first: 300000,  second: 97000,  third: 48000,  fourth: 30000, fifth: 18000 },
  'Group 1':    { first: 1000000, second: 325000, third: 160000, fourth: 100000,fifth: 60000 },
};

const fnRegion = getFunctions(undefined, 'australia-southeast1');

function todayISO() {
  // Use local timezone so AEST/AEDT users don't get yesterday's UTC date
  const now    = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  const local  = new Date(now.getTime() - offset);
  return local.toISOString().split('T')[0];
}

/**
 * Returns a default price lock time for a given meeting date.
 * Falls back to noon on the meeting date — a safe starting point.
 */
function defaultPriceLockTime(meetingDate) {
  const dateStr = meetingDate || new Date().toISOString().split('T')[0];
  return `${dateStr}T12:00`;
}

/**
 * Auto-calculates the price lock time from the first race's startTimestamp.
 * Locks prices 30 minutes before Race 1's jump — result is in the admin's local time.
 *
 * Requires startTimestamp (Firestore Timestamp from Birdcage) to be present on
 * the first race; falls back to bare scheduledJump string arithmetic if not.
 */
function priceLockFromFirstRace(races, meetingDate) {
  const dateStr = meetingDate || new Date().toISOString().split('T')[0];

  const sortedRaces = (races || [])
    .filter(r => r.startTimestamp || r.scheduledJump)
    .sort((a, b) => (a.raceNumber || 0) - (b.raceNumber || 0));

  if (sortedRaces.length === 0) return defaultPriceLockTime(dateStr);

  const firstRace = sortedRaces[0];

  // Preferred path: use startTimestamp (timezone-correct Firestore Timestamp)
  const jumpMs = getRace1JumpMillis(firstRace);
  if (jumpMs !== null) {
    const lockMs = jumpMs - 30 * 60 * 1000;
    const d = new Date(lockMs);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  // Fallback: bare scheduledJump string (inaccurate for interstate venues — same
  // bug we're fixing, but better than nothing if startTimestamp is missing)
  const firstJump = firstRace.scheduledJump;
  if (!firstJump) return defaultPriceLockTime(dateStr);

  if (firstJump.includes('T') || firstJump.includes('Z')) {
    const fMs = new Date(firstJump).getTime();
    const lMs = fMs - 30 * 60 * 1000;
    const lDate = new Date(lMs);
    const offset = lDate.getTimezoneOffset() * 60000;
    return new Date(lMs - offset).toISOString().slice(0, 16);
  }

  const [h, m] = firstJump.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return defaultPriceLockTime(dateStr);
  const totalMinutes = h * 60 + m - 30;
  const lockHour = Math.floor(totalMinutes / 60);
  const lockMin  = totalMinutes % 60;
  return `${dateStr}T${String(lockHour).padStart(2, '0')}:${String(lockMin).padStart(2, '0')}`;
}

function calculatePerRaceFloor(avgSpend, raceCount) {
  return Math.round((avgSpend * 20) / (20 + raceCount) / 1000) * 1000;
}

function calculateMaxPerRace(totalBankroll, raceCount, minSpend) {
  if (raceCount <= 1) return totalBankroll;
  return totalBankroll - (raceCount - 1) * minSpend;
}

const INITIAL_CONFIG = {
  gameName:             '',
  notifyPlayers:        false,
  tournamentId:         '',
  meetingDate:          todayISO(),
  selectedRaces:        [],
  avgSpendPerRace:      100000,
  minSpendPerRace:      50000,
  defaultPrizePool:     { ...DEFAULT_PRIZE_POOL },
  playerPrizePool:      0,
  featureRaceOverrides: {},
  prizeDescription:     '',
  priceLockTime:        defaultPriceLockTime(todayISO()),
  entriesOpenMode:      'immediate',
  maxPlayers:           null, // null = unlimited; integer = hard cap
  // New fields
  gameMode:             'solo',
  kMultiplier:          2.900,
  entryCloseMode:       'first_race',
  gameOpenTime:         '', // overridden to now+15min via lazy useState initializer on mount
  gameCloseTime:        '',
  roundNumber:          null, // auto-set when linking to an existing tournament
  tournamentName:       '',
  tournamentSeason:     new Date().getFullYear().toString(),
  tournamentStartDate:  '',
  tournamentEndDate:    '',
  tournamentFinalDate:  '',
  tournamentFrequency:  'weekly',
  qualifierCount:       '10',
  qualifierMode:        'count',
};

// ─── Prize Helpers ───────────────────────────────────────────────────────────────

function getOrdinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const DEFAULT_PRIZES = [
  { place: 1, description: '' },
  { place: 2, description: '' },
  { place: 3, description: '' },
  { place: 4, description: '' },
  { place: 5, description: '' },
];

// ─── Step 5 Timing Helpers ───────────────────────────────────────────────────────

/**
 * Converts a Firestore Timestamp (or bare string legacy value) back to a
 * datetime-local string in the admin's browser-local timezone. All datetime
 * inputs are admin-local by convention, so simple local getters are correct.
 */
function timestampToLocalInputString(val) {
  if (!val) return '';
  const date = typeof val?.toDate === 'function' ? val.toDate() : new Date(val);
  if (isNaN(date.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Converts any timing value to UTC milliseconds. Handles:
 *   - number:                    UTC millis from computeCorrectStartMs (our format)
 *   - Firestore Timestamp:       .toMillis()
 *   - {seconds, nanoseconds}:    serialised Timestamp POJO (e.g. after edit-reload)
 *   - ISO string / Date:         Date.parse
 */
function tsToMillis(ts) {
  if (ts === null || ts === undefined) return null;
  if (typeof ts === 'number')          return ts;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number')  return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6;
  const ms = new Date(ts).getTime();
  return isNaN(ms) ? null : ms;
}

/**
 * Returns "now + 15 minutes" as a datetime-local string in the admin's local timezone.
 * Used as the default Game Open time for new games.
 */
function getDefaultGameOpenTime() {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 60);
  const pad = n => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/**
 * Extracts Race 1's jump time as UTC milliseconds.
 *
 * SOURCE PRIORITY (most → least accurate):
 *   1. race.startTimestamp  — Firestore Timestamp set by Birdcage scraper,
 *      timezone-correct (e.g. "13 June 2026 at 23:39 UTC+10"). USE THIS.
 *   2. runner.startTimestamp — same instant on any runner in the race,
 *      promoted to race-level by fetchAvailableRaces but kept as a fallback.
 *   3. null — bare startTime/scheduledJump strings ("13:39") have NO
 *      timezone info and MUST NOT be used here (they cause the wrong-time bug).
 */
function getRace1JumpMillis(race1) {
  if (!race1) return null;

  const raceMs = tsToMillis(race1.startTimestamp);

  // ── DEBUG LOGGING — remove once countdown is confirmed correct ──────────
  console.log('[DEBUG race1] scheduledJump (bare local):', race1.scheduledJump);
  console.log('[DEBUG race1] startTimestamp (raw):', race1.startTimestamp);
  console.log('[DEBUG race1] startTimestamp type:', typeof race1.startTimestamp);
  console.log('[DEBUG race1] raceMs:', raceMs);
  if (raceMs !== null) {
    console.log('[DEBUG race1] as local Date:', new Date(raceMs).toString());
    console.log('[DEBUG race1] as ISO UTC:   ', new Date(raceMs).toISOString());
  }
  // ────────────────────────────────────────────────────────────────────────

  if (raceMs !== null) return raceMs;

  // Runner-level fallback (same race, any runner carries the same startTimestamp)
  if (race1.runners?.length > 0) {
    for (const runner of race1.runners) {
      const runnerMs = tsToMillis(runner.startTimestamp);
      if (runnerMs !== null) {
        console.log('[DEBUG race1] used runner startTimestamp:', runnerMs, new Date(runnerMs).toISOString());
        return runnerMs;
      }
    }
  }

  // No timezone-correct source — countdown shows "—"
  console.warn(
    '[AdminSetupPage] Race 1 has no startTimestamp — countdown shows "—". ' +
    'startTimestamp should be a UTC-millis number set by computeCorrectStartMs in adminFirestore.js.'
  );
  return null;
}

/**
 * Returns the EARLIEST startTimestamp (UTC millis) across ALL selected legs.
 * Used for Game Close Time default (first race to jump).
 */
function getEarliestLegMillis(races) {
  if (!races || races.length === 0) return null;
  let earliest = null;
  for (const leg of races) {
    const millis = getRace1JumpMillis(leg);
    if (millis === null) {
      console.warn(
        '[AdminSetup] getEarliestLegMillis: could not read startTimestamp for leg',
        leg.id ?? leg.raceNumber ?? '(unknown)',
        '— this leg is excluded from the earliest-jump calculation.',
      );
      continue;
    }
    if (earliest === null || millis < earliest) earliest = millis;
  }
  return earliest;
}

/**
 * Returns the LATEST startTimestamp (UTC millis) across ALL selected legs.
 * Used for Share Price Lock Time default — trading closes at the last race's jump.
 */
function getLatestLegMillis(races) {
  if (!races || races.length === 0) return null;
  let latest = null;
  for (const leg of races) {
    const millis = getRace1JumpMillis(leg);
    if (millis === null) continue;
    if (latest === null || millis > latest) latest = millis;
  }
  return latest;
}

/**
 * Returns a human-readable countdown from an admin-local datetime-local
 * string to the first jump time (in milliseconds). Examples:
 *   "58min until first jump" / "23hr 40min until first jump" / "3min after first jump"
 */
function formatCountdownToRace1(dateTimeLocalString, race1JumpMillis) {
  if (!dateTimeLocalString || race1JumpMillis === null) return '—';
  const enteredMs = new Date(dateTimeLocalString).getTime();
  if (isNaN(enteredMs)) return '—';

  const diffMs = race1JumpMillis - enteredMs; // positive = before Race 1
  const absMs = Math.abs(diffMs);
  const totalMinutes = Math.round(absMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}hr`);
  parts.push(`${minutes}min`);
  const timeStr = parts.join(' ');

  return diffMs >= 0 ? `${timeStr} until first jump` : `${timeStr} after first jump`;
}

// ─── UI Atomic Utility Elements ──────────────────────────────────────────────────

const SectionHeader = ({ number, title, subtitle }) => (
  <div className="ss-admin-section-header">
    <div className="ss-admin-section-num">{number}</div>
    <div>
      <h2 className="ss-admin-section-title">{title}</h2>
      {subtitle && <p className="ss-admin-section-sub">{subtitle}</p>}
    </div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div className="ss-admin-field">
    <label className="ss-admin-label">{label}</label>
    {children}
    {hint && <p className="ss-admin-hint">{hint}</p>}
  </div>
);

const NumberInput = ({ value, onChange, min, max, step, prefix }) => (
  <div className="ss-admin-number-wrap">
    {prefix && <span className="ss-admin-prefix">{prefix}</span>}
    <input
      type="number"
      className="ss-admin-input ss-admin-input--number"
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      min={min}
      max={max}
      step={step}
    />
  </div>
);

// ─── Section 3: Spend Config Sub-component ───────────────────────────────────────

const SpendConfigSection = ({ config, onChange }) => {
  const N   = config.selectedRaces.length || 0;
  const avg = config.avgSpendPerRace || 100000;
  const min = config.minSpendPerRace || 50000;
  const totalBankroll = avg * (N || 1);
  const formulaFloor = calculatePerRaceFloor(avg, N || 1);
  const maxPerRace = N > 0 ? calculateMaxPerRace(totalBankroll, N, min) : null;

  const handleAvgChange = useCallback((newAvg) => {
    onChange('avgSpendPerRace', newAvg);
    onChange('minSpendPerRace', Math.round(newAvg * 0.5 / 1000) * 1000);
  }, [onChange]);

  return (
    <section className="ss-admin-section">
      <SectionHeader number="3" title="Spend Configuration" subtitle="Sets each player's virtual bankroll rules." />
      <div className="ss-admin-spend-grid">
        <div className="ss-admin-field">
          <label className="ss-admin-label">Average spend per race</label>
          <NumberInput value={avg} onChange={handleAvgChange} min={1000} step={1000} prefix="$" />
          <p className="ss-admin-hint">{`Total bankroll per player = avg × races${N > 0 ? ` = $${totalBankroll.toLocaleString('en-AU')}` : ''}`}</p>
        </div>
        <div className="ss-admin-field">
          <label className="ss-admin-label">Minimum spend per race</label>
          <NumberInput value={min} onChange={v => onChange('minSpendPerRace', v)} min={0} max={avg} step={1000} prefix="$" />
          <p className="ss-admin-hint">Floor ensures players engage with every leg.</p>
        </div>
      </div>
      <div className="ss-admin-spend-derived">
        {N > 0 ? (
          <div className="ss-admin-max-per-race">
            <div className="ss-admin-max-label">Maximum on any single race</div>
            <div className="ss-admin-max-value">${maxPerRace.toLocaleString('en-AU')}</div>
            <div className="ss-admin-max-formula">${totalBankroll.toLocaleString('en-AU')} total − ({N - 1} legs × ${min.toLocaleString('en-AU')} floor)</div>
          </div>
        ) : (
          <div className="ss-admin-max-pending">Select races in Section 2 to see the calculation.</div>
        )}
      </div>
      <div className="ss-admin-formula-box">
        <div className="ss-admin-formula-header">
          <span className="ss-admin-formula-icon">📊</span>
          <span className="ss-admin-formula-title">Formula Recommendation</span>
          <span className="ss-admin-formula-badge">GUIDE ONLY</span>
        </div>
        <div className="ss-admin-formula-body">
          <div className="ss-admin-formula-row">
            <span>Recommended floor</span>
            <strong>${formulaFloor.toLocaleString('en-AU')}</strong>
          </div>
          <p className="ss-admin-formula-note">
            Based on <code>avg × 20 ÷ (20 + {N || '?'} races)</code>. Your minimum overrides this.
          </p>
        </div>
        <button className="ss-admin-formula-apply" onClick={() => onChange('minSpendPerRace', formulaFloor)} type="button">
          Apply recommended floor
        </button>
      </div>

      {/* K Multiplier Slider */}
      <Field
        label="K Multiplier — Share Price Calibration"
        hint="Controls the base price level for all shares in this game. Higher K = more expensive shares, higher prize exposure per player. 2.900 — Standard (recommended for $100k avg spend). Range: 1.500–4.000."
      >
        <div className="ss-k-slider-wrap">
          <div className="ss-k-slider-labels">
            <span>1.500 — Low</span>
            <span className="ss-k-value">{Number(config.kMultiplier ?? 2.900).toFixed(3)}</span>
            <span>4.000 — High</span>
          </div>
          <input
            type="range"
            className="ss-k-slider"
            min="1.5"
            max="4.0"
            step="0.001"
            value={config.kMultiplier ?? 2.900}
            onChange={e => onChange('kMultiplier', parseFloat(e.target.value))}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button type="button" className="ss-k-reset" onClick={() => onChange('kMultiplier', 2.900)}>
              Reset to default (2.900)
            </button>
            <span className="ss-k-formula">
              Formula: Base price = {Number(config.kMultiplier ?? 2.900).toFixed(3)} × AvgSpend × (1/Odds)<sup>0.991</sup> ÷ 100
            </span>
          </div>
        </div>
      </Field>
    </section>
  );
};

// ─── Section 4: Prize Structure Sub-components ───────────────────────────────────

const PrizeRow = ({ position, value, suggested, onChange, onApplySuggestion }) => {
  const isFirst = position === 'first';
  const hasSuggestion = !isFirst && suggested !== undefined && suggested !== value;

  return (
    <div className="ss-admin-prize-row">
      <span className="ss-admin-prize-pos">{POSITION_LABELS[position]}</span>
      <div className="ss-admin-prize-input-wrap">
        <div className="ss-admin-number-wrap">
          <span className="ss-admin-prefix">$</span>
          {/* min=0 and step=500 accepts all preset values without HTML validation conflicts */}
          <input
            type="number"
            className="ss-admin-input ss-admin-input--number"
            value={value}
            onChange={e => onChange(position, Number(e.target.value))}
            step={500}
            min={0}
          />
        </div>
        {hasSuggestion && (
          <button className="ss-admin-prize-suggest-btn" onClick={onApplySuggestion} type="button">
            → Suggested: ${suggested.toLocaleString('en-AU')}
          </button>
        )}
      </div>
    </div>
  );
};

const RacePrizeOverride = ({ raceId, raceName, basePool, overrides, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const pool = overrides[raceId] || basePool;
  const hasOverride = !!overrides[raceId];
  return (
    <div className={`ss-admin-race-prize-override ${hasOverride ? 'ss-admin-race-prize-override--active' : ''}`}>
      <div className="ss-admin-race-prize-header">
        <span className="ss-admin-race-prize-name">{raceName}</span>
        <button className={`ss-admin-race-prize-toggle ${hasOverride ? 'ss-admin-race-prize-toggle--active' : ''}`} onClick={() => setIsExpanded(e => !e)} type="button">
          {hasOverride ? '💰 Custom prizes' : '+ Custom prizes'}
        </button>
        {hasOverride && (
          <button className="ss-admin-race-prize-reset" onClick={() => onChange(raceId, null)} type="button">✕ Default</button>
        )}
      </div>
      {isExpanded && (
        <div className="ss-admin-race-prize-editor">
          <div className="ss-race-prize-presets">
            {Object.entries(PRIZE_PRESETS).map(([label, prizes]) => (
              <button
                key={label}
                type="button"
                className="ss-race-prize-preset-btn"
                onClick={() => onChange(raceId, { ...prizes })}
              >
                {label}
              </button>
            ))}
          </div>
          {['first', 'second', 'third', 'fourth', 'fifth'].map(pos => (
            <div key={pos} className="ss-admin-race-prize-mini-row">
              <span className="ss-admin-prize-pos">{POSITION_LABELS[pos]}</span>
              <div className="ss-admin-number-wrap">
                <span className="ss-admin-prefix">$</span>
                <input type="number" className="ss-admin-input" value={pool[pos] || 0} onChange={e => onChange(raceId, { ...pool, [pos]: Number(e.target.value) })} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PrizeStructureSection = ({ config, onChange }) => {
  const [applyPrizeTo, setApplyPrizeTo] = useState('all');
  const pool = config.defaultPrizePool;
  const playerPrizePool = config.playerPrizePool ?? 0;
  const suggestions = useMemo(() => {
    const first = pool.first || 500000;
    return Object.fromEntries(Object.entries(PRIZE_RATIOS).map(([pos, ratio]) => [pos, Math.round(first * ratio / 1000) * 1000]));
  }, [pool.first]);
  const totalPool = Object.values(pool).reduce((a, b) => a + b, 0);

  const suggestionsMatchCurrent = Object.entries(PRIZE_RATIOS).every(([pos]) => suggestions[pos] === pool[pos]);

  return (
    <section className="ss-admin-section">
      <SectionHeader number="4" title="Prize Structure" subtitle="Virtual prize money allocations per finishing tier." />

      {/* Real money prize pool on offer to players */}
      <div className="ss-admin-field" style={{ marginBottom: 24 }}>
        <label className="ss-admin-label" htmlFor="playerPrizePool">
          Player Prize Pool ($)
        </label>
        <input
          id="playerPrizePool"
          type="number"
          min="0"
          step="0.01"
          className="ss-admin-input"
          style={{ maxWidth: 200 }}
          value={playerPrizePool}
          onChange={e => onChange('playerPrizePool', parseFloat(e.target.value) || 0)}
          placeholder="0"
        />
        <p className="ss-admin-hint">
          Real money available to winning players. Leave as $0 for virtual-only games. This is shown on the Showdowns card.
        </p>
      </div>

      {/* Prize class preset buttons */}
      <div className="ss-prize-presets">
        <span className="ss-prize-presets-label">Quick presets:</span>
        {Object.entries(PRIZE_PRESETS).map(([label, prizes]) => (
          <button
            key={label}
            type="button"
            className="ss-prize-preset-btn"
            onClick={() => onChange('defaultPrizePool', prizes)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Apply mode toggle */}
      <div className="ss-prize-mode">
        <label className="ss-prize-mode-opt">
          <input type="radio" name="prizeMode" value="all"
            checked={applyPrizeTo === 'all'}
            onChange={() => setApplyPrizeTo('all')} />
          Apply same prize structure to all races
        </label>
        <label className="ss-prize-mode-opt">
          <input type="radio" name="prizeMode" value="per-race"
            checked={applyPrizeTo === 'per-race'}
            onChange={() => setApplyPrizeTo('per-race')} />
          Set individual prize per race
        </label>
      </div>

      <div className="ss-admin-prize-grid">
        <PrizeRow position="first" value={pool.first} onChange={(p, v) => onChange('defaultPrizePool', { ...pool, [p]: v })} />
        {['second', 'third', 'fourth', 'fifth'].map(pos => (
          <PrizeRow key={pos} position={pos} value={pool[pos]} suggested={suggestions[pos]} onChange={(p, v) => onChange('defaultPrizePool', { ...pool, [p]: v })} onApplySuggestion={() => onChange('defaultPrizePool', { ...pool, [pos]: suggestions[pos] })} />
        ))}
      </div>
      {!suggestionsMatchCurrent && (
        <button className="ss-admin-btn ss-admin-btn--secondary ss-admin-prize-apply-all" onClick={() => onChange('defaultPrizePool', { ...pool, second: suggestions.second, third: suggestions.third, fourth: suggestions.fourth, fifth: suggestions.fifth })} type="button">
          ↳ Apply all recommended values based on 1st place
        </button>
      )}
      <div className="ss-admin-prize-total">Total prize pool per race: <strong>${totalPool.toLocaleString('en-AU')}</strong></div>
      {config.selectedRaces?.length > 0 && (
        <div className="ss-admin-per-race-prizes">
          <span className="ss-admin-per-race-title">Feature Race Prize Overrides</span>
          {config.selectedRaces.map(race => (
            <RacePrizeOverride key={race.id} raceId={race.id} raceName={`R${race.raceNumber} — ${race.raceName || ''}`} basePool={pool} overrides={config.featureRaceOverrides || {}} onChange={(id, newPool) => {
              const current = { ...config.featureRaceOverrides };
              if (newPool === null) delete current[id]; else current[id] = newPool;
              onChange('featureRaceOverrides', current);
            }} />
          ))}
          {Object.keys(config.featureRaceOverrides || {}).length > 0 && (
            <label className="ss-admin-apply-all-check">
              <input type="checkbox" onChange={e => { if (e.target.checked) onChange('featureRaceOverrides', {}); }} />
              <span>Reset all races to default prize pool above</span>
            </label>
          )}
        </div>
      )}
    </section>
  );
};

// ─── Main Controller Module Page ─────────────────────────────────────────────────

export const AdminSetupPage = () => {
  const navigate = useNavigate();
  const { slateId: editSlateId } = useParams();
  const [searchParams] = useSearchParams();
  // Present when arriving from Admin > Schedule > "Create Showdown" on a
  // scheduled (not-yet-converted) upcoming_meetings doc — see
  // AdminSchedulePage.jsx / upcoming_meetings feature. Used to (a) prefill
  // the game name + meeting date below, and (b) link the meeting back to
  // this slate once it's saved, so it stops showing as "Planned" to users.
  const fromMeetingKey = searchParams.get('fromMeeting') || '';
  const [isEditMode, setIsEditMode] = useState(false);
  // Lazy initializer: runs once per component mount, not once per module load.
  // This ensures gameOpenTime defaults to the actual current time + 15min when
  // the admin navigates to New Game, not when the JS bundle was first parsed.
  const [config, setConfig] = useState(() => {
    const prefillTrackName = searchParams.get('trackName') || '';
    const prefillMeetingDate = searchParams.get('meetingDate') || '';
    return {
      ...INITIAL_CONFIG,
      gameOpenTime: getDefaultGameOpenTime(),
      ...(prefillTrackName ? { gameName: `${prefillTrackName} Showdown` } : {}),
      ...(prefillMeetingDate ? { meetingDate: prefillMeetingDate } : {}),
    };
  });
  const [submitState, setSubmitState] = useState('idle');
  const [errorMsg, setErrorMsg] = useState(null);

  const [feedMeetings, setFeedMeetings] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState('');
  const [codeFilter, setCodeFilter] = useState('ALL');
  const [pendingRaceSelections, setPendingRaceSelections] = useState(new Set());

  // Prize format — free-text descriptions per place (stored as prizes[] on game_slate)
  const [prizes, setPrizes] = useState(DEFAULT_PRIZES);

  // "Touched" flags: true once the admin manually edits a field.
  // While false, Close/Lock recalculate automatically whenever races change.
  // While true, manual edits are preserved and not overwritten.
  const [gameCloseTouched, setGameCloseTouched] = useState(false);
  const [lockTimeTouched,  setLockTimeTouched]  = useState(false);
  const [gameNameTouched,  setGameNameTouched]  = useState(false);

  // ── Existing-tournament linking ────────────────────────────────────────────
  const [addToExisting,       setAddToExisting]       = useState(false);
  const [existingTournaments, setExistingTournaments] = useState([]);
  const [linkTournamentId,    setLinkTournamentId]    = useState('');
  const [linkTournament,      setLinkTournament]      = useState(null);
  const [nextRoundNumber,     setNextRoundNumber]     = useState(null);

  const onChange = useCallback((field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  }, []);

  // Land at the top of the page on mount — without this, arriving here via
  // Admin > Schedule > "Create Showdown"/"View Showdown" (or the New Game
  // link) keeps the scroll position from whichever page/list the admin
  // scrolled to before navigating, landing on this long wizard mid-page.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Fetch tournament list whenever admin switches to tournament mode.
  useEffect(() => {
    if (config.gameMode !== 'tournament') return;
    getDocs(query(collection(db, 'tournaments'), orderBy('createdAt', 'desc'), limit(30)))
      .then(snap => setExistingTournaments(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(err => console.warn('[AdminSetup] Could not fetch tournaments:', err.message));
  }, [config.gameMode]);

  // When a tournament is selected, count existing legs to compute next round number.
  useEffect(() => {
    if (!linkTournamentId) { setNextRoundNumber(null); return; }
    getDocs(query(collection(db, 'game_slates'), where('tournamentId', '==', linkTournamentId)))
      .then(snap => setNextRoundNumber(snap.size + 1))
      .catch(err => console.warn('[AdminSetup] Could not count rounds:', err.message));
  }, [linkTournamentId]);

  // Auto-suggest game name when linking to an existing tournament.
  useEffect(() => {
    if (!addToExisting || !linkTournament || !nextRoundNumber) return;
    if (gameNameTouched) return; // don't overwrite a manually entered name
    onChange('gameName', `${linkTournament.tournamentName} Round ${nextRoundNumber}`);
  }, [addToExisting, linkTournament, nextRoundNumber, gameNameTouched, onChange]);


  /**
   * Recalculates Game Close and Share Price Lock defaults whenever races change.
   *   Game Close Time  → earliest leg jump (first race to run)
   *   Share Price Lock → latest leg jump  (last race to run — outer trading ceiling)
   * Skips any field the admin has already manually edited.
   */
  function recalculateTimingDefaults(allLegs) {
    const earliestMs = getEarliestLegMillis(allLegs);
    const latestMs   = getLatestLegMillis(allLegs);
    if (!gameCloseTouched && earliestMs !== null) {
      onChange('gameCloseTime', timestampToLocalInputString(new Date(earliestMs)));
    }
    if (!lockTimeTouched && latestMs !== null) {
      onChange('priceLockTime', timestampToLocalInputString(new Date(latestMs)));
    }
  }

  function handleEntryCloseModeChange(mode) {
    onChange('entryCloseMode', mode);
    const legs = config.selectedRaces || [];
    if (mode === 'first_race') {
      const ms = getEarliestLegMillis(legs);
      if (ms !== null) onChange('priceLockTime', timestampToLocalInputString(new Date(ms)));
    } else if (mode === 'per_race') {
      const ms = getLatestLegMillis(legs);
      if (ms !== null) onChange('priceLockTime', timestampToLocalInputString(new Date(ms)));
    }
  }

  // Countdown column anchor = EARLIEST jump across all legs (Option B).
  // Keeps the countdown consistent with the Close/Lock defaults: "X until first jump"
  // reads correctly whether or not LEG 1 (by addition order) is the first to jump.
  const race1JumpMillis = useMemo(() => {
    return getEarliestLegMillis(config.selectedRaces || []);
  }, [config.selectedRaces]);

  const updateSelectedRaceData = (index, field, value) => {
    const next = [...config.selectedRaces];
    next[index] = { ...next[index], [field]: value };
    onChange('selectedRaces', next);
  };

  useEffect(() => {
    if (!editSlateId) return;

    async function loadForEdit() {
      const snap = await getDoc(doc(db, 'game_slates', editSlateId));
      if (!snap.exists()) return;
      const data = snap.data();

      // ── Load selected races ────────────────────────────────────────────────
      // Prefer the compact snapshot saved on the parent doc (new games).
      // Fall back to the races subcollection for games saved before this fix.
      let selectedRaces = data.selectedRaces || [];
      if (selectedRaces.length === 0) {
        const racesSnap = await getDocs(
          query(collection(db, 'game_slates', editSlateId, 'races'))
        );
        selectedRaces = racesSnap.docs
          .map(d => d.data())
          .sort((a, b) => {
            // Prefer legOrder (written by new saves); fall back to raceNumber
            if (a.legOrder !== undefined && b.legOrder !== undefined) return a.legOrder - b.legOrder;
            return (a.raceNumber || 0) - (b.raceNumber || 0);
          });
      }

      setConfig(prev => ({
        ...prev,
        gameName:              data.gameName              || '',
        tournamentId:          data.tournamentId          || '',
        meetingDate:           data.meetingDate           || todayISO(),
        selectedRaces,
        avgSpendPerRace:       data.avgSpendPerRace       || 100000,
        minSpendPerRace:       data.minSpendPerRace       || 50000,
        defaultPrizePool:      data.defaultPrizePool      || { ...DEFAULT_PRIZE_POOL },
        playerPrizePool:       data.playerPrizePool       ?? 0,
        featureRaceOverrides:  data.featureRaceOverrides  || {},
        priceLockTime:  timestampToLocalInputString(data.priceLockTime)  || defaultPriceLockTime(data.meetingDate),
        maxPlayers:     data.maxPlayers ?? null,
        gameMode:       data.gameMode       || 'solo',
        kMultiplier:    data.kMultiplier    ?? 2.9,
        entryCloseMode: data.entryCloseMode || 'first_race',
        gameOpenTime:   timestampToLocalInputString(data.gameOpenTime)   || '',
        gameCloseTime:  timestampToLocalInputString(data.gameCloseTime)  || '',
        tournamentName:      data.tournamentName      || '',
        tournamentSeason:    data.tournamentSeason    || new Date().getFullYear().toString(),
        tournamentStartDate: data.tournamentStartDate || '',
        tournamentEndDate:   data.tournamentEndDate   || '',
        tournamentFinalDate: data.tournamentFinalDate || '',
        tournamentFrequency: data.tournamentFrequency || 'weekly',
        qualifierCount:      data.qualifierCount      || '10',
        qualifierMode:       data.qualifierMode       || 'count',
      }));

      setIsEditMode(true);

      // Prevent auto-suggest and timing recalculation from overwriting loaded values
      setGameNameTouched(true);
      setGameCloseTouched(true);
      setLockTimeTouched(true);

      setPrizes(data.prizes?.length > 0 ? data.prizes : DEFAULT_PRIZES);
    }

    loadForEdit().catch(err => console.error('[AdminSetup] loadForEdit error:', err));
  }, [editSlateId]);

  const loadScraperFeed = async () => {
    // Reset timing fields and touched flags whenever a new date's feed is
    // loaded — forces fresh defaults when the admin adds races from this feed.
    onChange('gameCloseTime', '');
    onChange('priceLockTime', '');
    setGameCloseTouched(false);
    setLockTimeTouched(false);

    setLoadingFeed(true);
    try {
      const [year, month, day] = config.meetingDate.split('-');
      const dateObj = new Date(year, month - 1, day);
      const monthShort = dateObj.toLocaleString('en-US', { month: 'short' });
      const scraperFormattedDate = `${year}${monthShort}${day}`;

      const meetings = await fetchAvailableRaces(scraperFormattedDate);
      setFeedMeetings(meetings);
    } catch (err) { console.error(err); }
    finally { setLoadingFeed(false); }
  };

  const selectedMeeting = useMemo(() => 
    feedMeetings.find(m => m.id === selectedMeetingId), 
  [feedMeetings, selectedMeetingId]);

  const toggleRaceSelection = (raceNumber) => {
    setPendingRaceSelections(prev => {
      const next = new Set(prev);
      if (next.has(raceNumber)) next.delete(raceNumber);
      else next.add(raceNumber);
      return next;
    });
  };

  const handleAddSelectedRaces = () => {
    if (!selectedMeeting) return;
    const racesToAdd = (selectedMeeting.races || []).filter(r => pendingRaceSelections.has(r.raceNumber));
    const formattedRaces = racesToAdd.map(r => ({
      id: r.id || `${selectedMeeting.id}_R${r.raceNumber}`,
      raceNumber: r.raceNumber,
      raceName: r.raceName || `Race ${r.raceNumber}`,
      distance: r.distance,
      // raceClass/classStr/racePrizemoney/going now come from the Racing
      // Australia Form Guide scrape when available — only fall back to the
      // generic 'Open' when the scraper genuinely has nothing (e.g. older
      // meetings scraped before this field existed). Feeding 'Open' over a
      // real Maiden/Handicap/Benchmark class was starving the v4 engine's
      // class-comparison factor of accurate context.
      raceClass: r.raceClass || 'Open',
      classStr: r.classStr || r.raceClass || null,
      racePrizemoney: r.racePrizemoney ?? null,
      going: r.going || null,
      meetingName: selectedMeeting.meetingName,
      meetingState: selectedMeeting.meetingState,
      scheduledJump: r.scheduledJump || '',
      // startTimestamp: timezone-correct Firestore Timestamp used for countdown
      // and default prefill of Game Close / Share Price Lock.
      startTimestamp: r.startTimestamp || null,
      runners: r.runners || [],
      status: 'building'
    }));

    const allRaces = [...config.selectedRaces, ...formattedRaces];
    onChange('selectedRaces', allRaces);

    // Recalculate Close/Lock from the full updated leg list.
    // Respects touched flags — won't overwrite fields the admin manually edited.
    recalculateTimingDefaults(allRaces);

    setPendingRaceSelections(new Set());
    setSelectedMeetingId('');
  };

  const removeRace = (index) => {
    const next = [...config.selectedRaces];
    next.splice(index, 1);
    onChange('selectedRaces', next);
    // Removing a leg may change which leg has the earliest jump — recalculate.
    recalculateTimingDefaults(next);
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    if (!config.gameName || config.selectedRaces.length === 0) {
      setErrorMsg('Please specify a game name and select at least one race leg.');
      return;
    }
    if (!config.defaultPrizePool?.first || config.defaultPrizePool.first <= 0) {
      setErrorMsg('First place prize must be greater than $0.');
      return;
    }

    setSubmitState('loading');
    try {
      const targetSlateId = editSlateId || `${config.meetingDate}_${config.gameName.replace(/\s+/g, '_').toLowerCase()}`;
      const slateRef = doc(db, 'game_slates', targetSlateId);
      
      const payload = {
        id: targetSlateId,
        gameName: config.gameName,
        meetingDate: config.meetingDate,
        totalRaces: config.selectedRaces.length,
        completedRaces: 0,
        avgSpendPerRace: config.avgSpendPerRace,
        minSpendPerRace: config.minSpendPerRace,
        defaultPrizePool: config.defaultPrizePool,
        playerPrizePool:  config.playerPrizePool ?? 0,
        featureRaceOverrides: config.featureRaceOverrides,
        priceLockTime: config.priceLockTime
          ? Timestamp.fromMillis(new Date(config.priceLockTime).getTime())
          : null,
        maxPlayers: config.maxPlayers,
        status: 'building',
        pricesLocked: false,
        entriesOpen: false,
        venueState: config.selectedRaces?.[0]?.meetingState || null,
        // New fields
        gameMode: config.gameMode || 'solo',
        kMultiplier: config.kMultiplier ?? 2.9,
        entryCloseMode: config.entryCloseMode || 'first_race',
        gameOpenTime: config.gameOpenTime
          ? Timestamp.fromMillis(new Date(config.gameOpenTime).getTime())
          : null,
        gameCloseTime: config.gameCloseTime
          ? Timestamp.fromMillis(new Date(config.gameCloseTime).getTime())
          : null,
        prizes: prizes
          .filter(p => p.description.trim() !== '')
          .map(p => ({ place: p.place, description: p.description.trim() })),
        // Compact snapshot for edit-mode reload (no runner arrays to keep doc small)
        selectedRaces: config.selectedRaces.map(({ runners, ...rest }) => rest),
        notifyPlayers:      config.notifyPlayers || false,
        notifyScheduledFor: config.notifyPlayers && config.gameOpenTime
          ? Timestamp.fromMillis(new Date(config.gameOpenTime).getTime() - 30 * 60 * 1000)
          : null,
        updatedAt: serverTimestamp()
      };

      const batch = writeBatch(db);

      // ── Tournament handling ────────────────────────────────────────────────
      if (config.gameMode === 'tournament') {
        if (addToExisting && linkTournamentId && linkTournament) {
          // ── LINK TO EXISTING TOURNAMENT ──────────────────────────────────
          const roundNum = nextRoundNumber ?? 1;
          payload.tournamentId   = linkTournamentId;
          payload.roundNumber    = roundNum;
          // Copy tournament meta from the linked doc for quick reads
          payload.tournamentName     = linkTournament.tournamentName     || '';
          payload.tournamentSeason   = linkTournament.tournamentSeason   || '';
          payload.tournamentStartDate= linkTournament.startDate          || '';
          payload.tournamentEndDate  = linkTournament.endDate            || '';
          payload.tournamentFinalDate= linkTournament.grandFinalDate     || '';
          payload.tournamentFrequency= linkTournament.gameFrequency      || 'weekly';
          payload.qualifierCount     = String(linkTournament.qualifierCount || '10');
          payload.qualifierMode      = linkTournament.qualifierMode      || 'count';

          // Update the tournament doc: increment totalRounds, extend endDate if needed
          const tRef = doc(db, 'tournaments', linkTournamentId);
          const tUpdates = { totalRounds: roundNum, updatedAt: serverTimestamp() };
          if (config.meetingDate && config.meetingDate > (linkTournament.endDate || '')) {
            tUpdates.endDate = config.meetingDate;
          }
          batch.update(tRef, tUpdates);

        } else {
          // ── CREATE NEW TOURNAMENT ────────────────────────────────────────
          const tId = `${config.tournamentSeason || new Date().getFullYear()}_${
            config.tournamentName.replace(/\s+/g, '_').toLowerCase()
          }`;
          const tRef = doc(db, 'tournaments', tId);
          batch.set(tRef, {
            id:                   tId,
            tournamentName:       config.tournamentName     || '',
            tournamentSeason:     config.tournamentSeason   || '',
            startDate:            config.tournamentStartDate|| '',
            endDate:              config.tournamentEndDate  || '',
            grandFinalDate:       config.tournamentFinalDate|| '',
            gameFrequency:        config.tournamentFrequency|| 'weekly',
            qualifierCount:       config.qualifierCount     || '10',
            qualifierMode:        config.qualifierMode      || 'count',
            totalRounds:          1,
            createdAt:            serverTimestamp(),
            updatedAt:            serverTimestamp(),
          }, { merge: true });

          payload.tournamentId       = tId;
          payload.roundNumber        = 1;
          payload.tournamentName     = config.tournamentName     || '';
          payload.tournamentSeason   = config.tournamentSeason   || '';
          payload.tournamentStartDate= config.tournamentStartDate|| '';
          payload.tournamentEndDate  = config.tournamentEndDate  || '';
          payload.tournamentFinalDate= config.tournamentFinalDate|| '';
          payload.tournamentFrequency= config.tournamentFrequency|| 'weekly';
          payload.qualifierCount     = config.qualifierCount     || '10';
          payload.qualifierMode      = config.qualifierMode      || 'count';
        }
      }
      // ── (solo games: no tournament fields added) ───────────────────────────

      batch.set(slateRef, payload, { merge: true });

      config.selectedRaces.forEach((race, index) => {
        const raceRef = doc(db, 'game_slates', targetSlateId, 'races', race.id);
        batch.set(raceRef, { ...race, slateId: targetSlateId, legOrder: index }, { merge: true });
      });

      await batch.commit();

      // ── Link back to the scheduled meeting (Upcoming Schedule feature) ────
      // Only relevant when this save originated from Admin > Schedule >
      // "Create Showdown" on a still-scheduled meeting. Marks the meeting
      // showdown_created + convertedSlateId so it stops appearing as
      // "Planned" and instead reflects the real Showdown's live status.
      // Non-fatal: the slate itself is the source of truth, so a failure
      // here should never block publishing.
      if (fromMeetingKey && !editSlateId) {
        try {
          await linkMeetingToShowdown(fromMeetingKey, targetSlateId);
        } catch (linkErr) {
          console.error('[AdminSetup] Failed to link scheduled meeting to new Showdown:', linkErr);
        }
      }

      // ── Canonical rating-freshness gate ──────────────────────────────────
      // Never let a game go live on stale/thin race data. getRatedRaceForGame
      // validates enrichment + rating freshness for each published race and
      // transparently re-rates via the same pipeline as admin "Rate Runners"
      // if needed (see gameRatingLifecycle.js) — it only fails if the race
      // genuinely has no enriched form data anywhere to rate from.
      setSubmitState('validating');
      const getRatedRaceForGameFn = httpsCallable(fnRegion, 'getRatedRaceForGame');
      const raceIssues = [];
      for (const race of config.selectedRaces) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await getRatedRaceForGameFn({ slateId: targetSlateId, raceId: race.id });
        } catch (raceErr) {
          raceIssues.push(`${race.raceName || race.id}: ${raceErr.message}`);
        }
      }

      if (raceIssues.length > 0) {
        // Belt-and-braces: explicitly flag the slate itself as blocked, even
        // though getRatedRaceForGame may have thrown before it could persist
        // ratingsStatus onto the individual race doc(s). This is what
        // AdminSlatesPage/AdminOddsPage's "STALE_OR_THIN_DATA" hard-block
        // checks read before allowing pricesLocked/entriesOpen/live.
        try {
          await updateDoc(slateRef, {
            ratingsCalculated: false,
            ratingsStatus:     'STALE_OR_THIN_DATA',
          });
        } catch (flagErr) {
          console.error('Failed to flag slate as STALE_OR_THIN_DATA:', flagErr);
        }

        setErrorMsg(
          `Game was saved but is BLOCKED from going live — ${raceIssues.length} race(s) could not be ` +
          `confirmed with fresh, enriched ratings. Use "♻ Refresh Form + Recalculate Ratings" on the ` +
          `Slates page, then re-open this game to publish, before locking prices or opening entries:\n${raceIssues.join('\n')}`
        );
        setSubmitState('idle');
        return;
      }

      navigate('/admin');
    } catch (err) { setErrorMsg(err.message); }
    finally { setSubmitState('idle'); }
  };

  return (
    <div className="ss-admin-setup-page">
      <div className="ss-admin-wizard-header">
        <h1 className="ss-admin-page-title">
          {isEditMode ? `Editing: ${config.gameName || 'Game'}` : 'New Game Setup'}
        </h1>
        {fromMeetingKey && !isEditMode && (
          <p className="ss-helper-text">
            📅 Converting a scheduled meeting from Upcoming Schedule — saving this game will
            automatically link it back and remove it from the "Planned" list.
          </p>
        )}
      </div>

      <form onSubmit={handlePublish} className="ss-admin-wizard-form">
        {/* Section 1: Game Identity */}
        <div className="ss-setup-section">
          <h3 className="ss-setup-section-title">
            <span className="ss-setup-section-num">1</span>
            Game Identity
          </h3>

          <div className="ss-setup-field-row">
            <Field label="Game Name">
              <input
                type="text"
                className="ss-admin-input"
                value={config.gameName}
                onChange={e => { onChange('gameName', e.target.value); setGameNameTouched(true); }}
                placeholder="e.g. Saturday Flemington Showdown"
              />
              {isEditMode && (
                <p className="ss-helper-text ss-helper-warning">
                  ⚠ Display name only — the internal game ID ({editSlateId}) cannot be changed.
                </p>
              )}
            </Field>
            <Field label="Meeting Date">
              <input
                type="date"
                className="ss-admin-input"
                value={config.meetingDate}
                onChange={e => {
                  onChange('meetingDate', e.target.value);
                  onChange('priceLockTime', `${e.target.value}T12:00`);
                }}
              />
            </Field>
          </div>

          <Field label="Game Mode">
            <div className="ss-mode-toggle">
              <button
                type="button"
                className={`ss-mode-btn ${config.gameMode !== 'tournament' ? 'active' : ''}`}
                onClick={() => onChange('gameMode', 'solo')}
              >
                🏇 SOLO GAME
                <span className="ss-mode-hint">Single standalone game</span>
              </button>
              <button
                type="button"
                className={`ss-mode-btn ${config.gameMode === 'tournament' ? 'active' : ''}`}
                onClick={() => onChange('gameMode', 'tournament')}
              >
                🏆 TOURNAMENT ROUND
                <span className="ss-mode-hint">Part of a multi-round series</span>
              </button>
            </div>
          </Field>

          {/* ── Existing-tournament linker — only visible in tournament mode ── */}
          {config.gameMode === 'tournament' && (
            <div className="ss-existing-tournament-row">
              <label className="ss-checkbox-label">
                <input
                  type="checkbox"
                  checked={addToExisting}
                  onChange={e => {
                    setAddToExisting(e.target.checked);
                    if (!e.target.checked) {
                      setLinkTournamentId('');
                      setLinkTournament(null);
                      setNextRoundNumber(null);
                      setGameNameTouched(false); // allow name to re-default for a new tournament
                    }
                  }}
                />
                <span>Add to existing tournament</span>
              </label>

              {addToExisting && (
                <>
                  <select
                    className="ss-tournament-select"
                    value={linkTournamentId}
                    onChange={e => {
                      const id = e.target.value;
                      setLinkTournamentId(id);
                      const found = existingTournaments.find(t => t.id === id) || null;
                      setLinkTournament(found);
                      setGameNameTouched(false); // allow auto-suggest when tournament changes
                    }}
                  >
                    <option value="">— Select a tournament —</option>
                    {existingTournaments.length === 0 && (
                      <option value="" disabled>No tournaments found</option>
                    )}
                    {existingTournaments.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.tournamentName} ({t.tournamentSeason}) — {t.gameFrequency}
                      </option>
                    ))}
                  </select>

                  {nextRoundNumber !== null && linkTournament && (
                    <p className="ss-round-badge">
                      📋 This will be added as <strong>Round {nextRoundNumber}</strong> of{' '}
                      <strong>{linkTournament.tournamentName}</strong>
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Max Players cap ─────────────────────────────────────────────── */}
          <Field label="Max Players"
                 hint="Cap the number of entries. Leave blank for unlimited.">
            <input
              type="number"
              min="1"
              max="100000"
              className="ss-admin-input"
              style={{ maxWidth: 200 }}
              placeholder="e.g. 500 (blank = unlimited)"
              value={config.maxPlayers === null || config.maxPlayers === undefined ? '' : config.maxPlayers}
              onChange={e => onChange('maxPlayers', e.target.value === '' ? null : parseInt(e.target.value, 10))}
            />
          </Field>

          {/* ── Prize Format ────────────────────────────────────────────────── */}
          <Field label="Prize Format"
                 hint="Describe each prize — cash, vouchers, horse shares, or anything else. Leave blank for places with no prize.">
            <div className="ss-prizes-list">
              {prizes.map((prize, index) => (
                <div key={prize.place} className="ss-prize-row">
                  <span className="ss-prize-place">{getOrdinal(prize.place)}</span>
                  <input
                    type="text"
                    className="ss-admin-input ss-input--prize"
                    placeholder={
                      prize.place === 1 ? 'e.g. $500 cash' :
                      prize.place === 2 ? 'e.g. $100 Coles gift card' :
                      prize.place === 3 ? 'e.g. 5% share in horse XYZ with Tony Gollan' :
                      'e.g. Prize description...'
                    }
                    value={prize.description}
                    onChange={e => {
                      const updated = [...prizes];
                      updated[index] = { ...prize, description: e.target.value };
                      setPrizes(updated);
                    }}
                  />
                  {prizes.length > 1 && (
                    <button
                      type="button"
                      className="ss-prize-remove"
                      onClick={() => {
                        const updated = prizes
                          .filter((_, i) => i !== index)
                          .map((p, i) => ({ ...p, place: i + 1 }));
                        setPrizes(updated);
                      }}
                      aria-label={`Remove ${getOrdinal(prize.place)} place`}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              className="ss-prize-add-btn"
              onClick={() => setPrizes([...prizes, { place: prizes.length + 1, description: '' }])}
            >
              + Add prize place
            </button>
          </Field>
        </div>

        <section className="ss-admin-section">
          <SectionHeader number="2" title="Race Selection" subtitle="Mix and match races from multiple meetings." />
          
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
            <button type="button" className="ss-admin-btn ss-admin-btn--secondary" onClick={loadScraperFeed}>
              {loadingFeed ? '⏳ Querying scraper...' : '🔄 Load Races from Data Feed'}
            </button>
            <span className="ss-admin-hint">for {config.meetingDate}</span>
          </div>

          {feedMeetings.length > 0 && (
            <div className="ss-admin-feed-selector-box">
              <div className="ss-code-filter-tabs">
                {['ALL', 'TB', 'HAR', 'GRY'].map(code => (
                  <button
                    key={code}
                    type="button"
                    className={`ss-code-filter-tab ${codeFilter === code ? 'active' : ''}`}
                    onClick={() => { setCodeFilter(code); setSelectedMeetingId(''); }}
                  >
                    {code === 'ALL' ? 'All Codes' : code === 'TB' ? '🏇 Thoroughbred' : code === 'HAR' ? '🐴 Harness' : '🐕 Greyhound'}
                  </button>
                ))}
              </div>
              <Field label="1. Select a Track/Meeting">
                <select className="ss-admin-input" value={selectedMeetingId} onChange={e => { setSelectedMeetingId(e.target.value); setPendingRaceSelections(new Set()); }}>
                  <option value="">— Choose a meeting —</option>
                  {feedMeetings
                    .filter(m => {
                      if (codeFilter === 'ALL') return true;
                      if (codeFilter === 'TB')  return m.code === 'T' || m.code === 'Thoroughbred';
                      if (codeFilter === 'HAR') return m.code === 'H' || m.code === 'Harness';
                      if (codeFilter === 'GRY') return m.code === 'G' || m.code === 'Greyhound';
                      return true;
                    })
                    .map(m => (
                      <option key={m.id} value={m.id}>{m.meetingName} ({m.meetingState}) — {m.races?.length || 0} races</option>
                    ))
                  }
                </select>
              </Field>

              {selectedMeeting && (
                <div className="ss-admin-race-picker">
                  <div className="ss-admin-race-picker-header">
                    <span className="ss-admin-label">2. Select Races to Add</span>
                    <div className="ss-admin-picker-actions">
                      <button type="button" onClick={() => setPendingRaceSelections(new Set(selectedMeeting.races.map(r=>r.raceNumber)))}>Select All</button>
                      <button type="button" onClick={() => setPendingRaceSelections(new Set())}>Clear</button>
                    </div>
                  </div>
                  <div className="ss-admin-race-grid">
                    {selectedMeeting.races.sort((a,b)=>a.raceNumber - b.raceNumber).map(r => (
                      <label key={r.raceNumber} className={`ss-admin-race-checkbox ${pendingRaceSelections.has(r.raceNumber) ? 'active' : ''}`}>
                        <input type="checkbox" checked={pendingRaceSelections.has(r.raceNumber)} onChange={() => toggleRaceSelection(r.raceNumber)} />
                        <span>R{r.raceNumber} <small>({r.distance}m)</small></span>
                      </label>
                    ))}
                  </div>
                  <button type="button" className="ss-admin-btn ss-admin-btn--primary ss-admin-add-races-btn" disabled={pendingRaceSelections.size === 0} onClick={handleAddSelectedRaces}>
                    + Add Selected Races to Game
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="ss-admin-selected-summary">
            <div className="ss-admin-summary-header">
              <span className="ss-admin-label">Tournament Legs ({config.selectedRaces.length})</span>
              {config.selectedRaces.length > 0 && (
                <button type="button" onClick={() => onChange('selectedRaces', [])} className="ss-admin-clear-all-btn">Clear All</button>
              )}
            </div>
            {config.selectedRaces.length > 0 ? (
              <ul className="ss-admin-leg-list">
                {config.selectedRaces.map((r, i) => (
                  <li key={i} className="ss-admin-leg-item ss-admin-leg-item--expanded">
                    <div className="ss-admin-leg-header-row">
                      <div className="ss-admin-leg-info">
                        <span className="ss-admin-leg-num">Leg {i + 1}</span>
                        <span className="ss-admin-leg-name">{r.meetingName} R{r.raceNumber}</span>
                      </div>
                      <button type="button" onClick={() => removeRace(i)} className="ss-admin-remove-leg-btn" title="Remove leg">✕</button>
                    </div>
                    <div className="ss-admin-leg-details-row">
                      <div className="ss-admin-leg-field">
                        <label>Jump Time (Local)</label>
                        <input type="time" className="ss-admin-input" value={r.scheduledJump || ''} onChange={e => updateSelectedRaceData(i, 'scheduledJump', e.target.value)} />
                      </div>
                      <div className="ss-admin-leg-field">
                        <label>Distance (m)</label>
                        <input type="number" className="ss-admin-input" value={r.distance || ''} onChange={e => updateSelectedRaceData(i, 'distance', Number(e.target.value))} />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="ss-admin-no-legs">No races added yet. Select from the feed above.</div>
            )}
          </div>
        </section>

        <SpendConfigSection config={config} onChange={onChange} />
        <PrizeStructureSection config={config} onChange={onChange} />

        {/* Section 5: Timing & Gating */}
        <div className="ss-setup-section">
          <h3 className="ss-setup-section-title">
            <span className="ss-setup-section-num">5</span>
            Timing &amp; Gating
          </h3>

          <p className="ss-section-note">
            ℹ️ Enter all times in <strong>your local time</strong>.
            The countdown column and Close/Lock defaults are anchored to the
            <strong> earliest jump</strong> across all selected legs.
            {!race1JumpMillis && config.selectedRaces?.length > 0 && (
              <span className="ss-section-note-warn"> (Add races with jump times to see countdowns.)</span>
            )}
          </p>

          {/* Game Open */}
          <div className="ss-timing-field">
            <label className="ss-admin-label">GAME OPEN DATE &amp; TIME</label>
            <p className="ss-admin-hint">When players can start entering</p>
            <div className="ss-timing-row">
              <input
                type="datetime-local"
                className="ss-admin-input ss-timing-input"
                value={config.gameOpenTime || ''}
                onChange={e => onChange('gameOpenTime', e.target.value)}
              />
              <div className="ss-timing-countdown">
                {formatCountdownToRace1(config.gameOpenTime, race1JumpMillis)}
              </div>
            </div>
          </div>

          {/* Game Close */}
          <div className="ss-timing-field">
            <label className="ss-admin-label">GAME CLOSE DATE &amp; TIME</label>
            <p className="ss-admin-hint">When no new entries are accepted</p>
            <div className="ss-timing-row">
              <input
                type="datetime-local"
                className="ss-admin-input ss-timing-input"
                value={config.gameCloseTime || ''}
                onChange={e => { onChange('gameCloseTime', e.target.value); setGameCloseTouched(true); }}
              />
              <div className="ss-timing-countdown">
                {formatCountdownToRace1(config.gameCloseTime, race1JumpMillis)}
              </div>
            </div>
          </div>

          {/* Share Price Lock Time */}
          <div className="ss-timing-field">
            <label className="ss-admin-label">SHARE PRICE LOCK TIME</label>
            <p className="ss-admin-hint">
              Absolute outer ceiling — no buying or selling after this point for ANY race.
              Defaults to the last race's jump time. Override only if you need an earlier cutoff.
            </p>
            <div className="ss-timing-row">
              <input
                type="datetime-local"
                className="ss-admin-input ss-timing-input"
                value={config.priceLockTime || ''}
                onChange={e => {
                  onChange('priceLockTime', e.target.value);
                  setLockTimeTouched(true);
                }}
              />
              <div className="ss-timing-countdown">
                {formatCountdownToRace1(config.priceLockTime, race1JumpMillis)}
              </div>
            </div>
            {config.priceLockTime && (() => {
              const hasPassed = new Date(config.priceLockTime) < new Date();
              if (hasPassed) {
                return (
                  <div className="ss-admin-countdown-hint ss-admin-countdown-hint--past">
                    ⚠ This time has already passed
                  </div>
                );
              }
              const countdown = getCountdownTo(new Date(config.priceLockTime).toISOString());
              return countdown
                ? <div className="ss-admin-countdown-hint">⏳ Trading closes in {countdown}</div>
                : null;
            })()}
            <p className="ss-setup-hint-block">
              ⏰ This is the outer trading ceiling. Players cannot buy, sell or transfer after this time.
              Under "first race" mode all races also lock at the first jump — this field is the final safety net.
            </p>
          </div>

          <Field label="Race-by-Race Entry Closing">
            <div className="ss-entry-close-group">
              <label className={`ss-entry-close-option ${config.entryCloseMode !== 'per_race' ? 'ss-entry-close-option--active' : ''}`}>
                <input
                  type="radio"
                  name="entryCloseMode"
                  value="first_race"
                  checked={config.entryCloseMode !== 'per_race'}
                  onChange={() => handleEntryCloseModeChange('first_race')}
                />
                <div className="ss-entry-close-content">
                  <strong>Lock at first race jump</strong>
                  <span>All shares lock when Race 1 of the game starts. Simpler.</span>
                </div>
              </label>
              <label className={`ss-entry-close-option ${config.entryCloseMode === 'per_race' ? 'ss-entry-close-option--active' : ''}`}>
                <input
                  type="radio"
                  name="entryCloseMode"
                  value="per_race"
                  checked={config.entryCloseMode === 'per_race'}
                  onChange={() => handleEntryCloseModeChange('per_race')}
                />
                <div className="ss-entry-close-content">
                  <strong>Close per individual race</strong>
                  <span>Each race locks shares individually at its own jump time. Allows later entries for later races.</span>
                </div>
              </label>
            </div>
          </Field>
        </div>

        {/* Section 6: Tournament Configuration — only visible in tournament mode */}
        {config.gameMode === 'tournament' && (
          <div className="ss-setup-section ss-setup-section--tournament">
            <h3 className="ss-setup-section-title">
              <span className="ss-setup-section-num">6</span>
              Tournament Configuration
            </h3>

            {addToExisting && linkTournament ? (
              /* ── READ-ONLY SUMMARY when linking to existing tournament ─── */
              <div className="ss-tournament-summary">
                <p className="ss-tournament-summary-title">
                  Linking to: <strong>{linkTournament.tournamentName}</strong>
                </p>
                <div className="ss-tournament-summary-grid">
                  <div className="ss-tournament-summary-row">
                    <span className="ss-tournament-summary-label">Season</span>
                    <span>{linkTournament.tournamentSeason || '—'}</span>
                  </div>
                  <div className="ss-tournament-summary-row">
                    <span className="ss-tournament-summary-label">Start Date</span>
                    <span>{linkTournament.startDate || '—'}</span>
                  </div>
                  <div className="ss-tournament-summary-row">
                    <span className="ss-tournament-summary-label">End Date</span>
                    <span>{linkTournament.endDate || '—'}</span>
                  </div>
                  <div className="ss-tournament-summary-row">
                    <span className="ss-tournament-summary-label">Grand Final</span>
                    <span>{linkTournament.grandFinalDate || '—'}</span>
                  </div>
                  <div className="ss-tournament-summary-row">
                    <span className="ss-tournament-summary-label">Frequency</span>
                    <span style={{ textTransform: 'capitalize' }}>{linkTournament.gameFrequency || '—'}</span>
                  </div>
                  <div className="ss-tournament-summary-row">
                    <span className="ss-tournament-summary-label">Qualifiers</span>
                    <span>
                      {linkTournament.qualifierCount || '—'}
                      {linkTournament.qualifierMode === 'percent' ? '%' : ' players'}
                    </span>
                  </div>
                </div>
                <p className="ss-section-note" style={{ marginTop: 12 }}>
                  ℹ️ This game will be added as <strong>Round {nextRoundNumber}</strong>.
                  The tournament&rsquo;s end date will auto-extend if this game&rsquo;s meeting date
                  falls after the current end date.
                </p>
              </div>
            ) : (
              /* ── FULL FORM for creating a brand-new tournament ─────────── */
              <>
                <div className="ss-setup-field-row">
                  <Field label="Tournament Name">
                    <input type="text" className="ss-admin-input"
                      value={config.tournamentName || ''}
                      onChange={e => onChange('tournamentName', e.target.value)}
                      placeholder="e.g. Winter Championship Series" />
                  </Field>
                  <Field label="Tournament Season">
                    <input type="text" className="ss-admin-input"
                      value={config.tournamentSeason || ''}
                      onChange={e => onChange('tournamentSeason', e.target.value)}
                      placeholder="e.g. 2026" />
                  </Field>
                </div>

                <div className="ss-setup-field-row">
                  <Field label="Tournament Start Date">
                    <input type="date" className="ss-admin-input"
                      value={config.tournamentStartDate || ''}
                      onChange={e => onChange('tournamentStartDate', e.target.value)} />
                  </Field>
                  <Field label="Tournament End Date">
                    <input type="date" className="ss-admin-input"
                      value={config.tournamentEndDate || ''}
                      onChange={e => onChange('tournamentEndDate', e.target.value)} />
                  </Field>
                  <Field label="Grand Final Date">
                    <input type="date" className="ss-admin-input"
                      value={config.tournamentFinalDate || ''}
                      onChange={e => onChange('tournamentFinalDate', e.target.value)} />
                  </Field>
                </div>

                <div className="ss-setup-field-row">
                  <Field label="Game Frequency">
                    <select className="ss-admin-input"
                      value={config.tournamentFrequency || 'weekly'}
                      onChange={e => onChange('tournamentFrequency', e.target.value)}>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="other">Other / Manual</option>
                    </select>
                  </Field>
                  <Field label="Qualifiers to Grand Final" hint="Number OR percentage of players who advance">
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input type="number" className="ss-admin-input" style={{ width: '80px' }}
                        value={config.qualifierCount || ''}
                        onChange={e => onChange('qualifierCount', e.target.value)}
                        placeholder="e.g. 10" />
                      <select className="ss-admin-input" style={{ flex: 1 }}
                        value={config.qualifierMode || 'count'}
                        onChange={e => onChange('qualifierMode', e.target.value)}>
                        <option value="count">Top N players</option>
                        <option value="percent">Top % of players</option>
                      </select>
                    </div>
                    <p className="ss-setup-hint-block">
                      e.g. "Top 10 players" or "Top 20% of players" from each round qualify.
                    </p>
                  </Field>
                </div>
              </>
            )}
          </div>
        )}

        {errorMsg && <div className="ss-admin-w-error" style={{ whiteSpace: 'pre-line' }}>⚠ {errorMsg}</div>}

        {/* Player notification checkbox */}
        <div style={{
          margin:       '0 0 20px',
          padding:      '18px 20px',
          background:   '#EFF6FF',
          border:       '1px solid #BFDBFE',
          borderRadius: '8px',
        }}>
          <label style={{
            display:    'flex',
            alignItems: 'flex-start',
            gap:        '12px',
            cursor:     'pointer',
          }}>
            <input
              type="checkbox"
              checked={config.notifyPlayers || false}
              onChange={e => onChange('notifyPlayers', e.target.checked)}
              style={{ marginTop: '3px', width: '16px', height: '16px', flexShrink: 0 }}
            />
            <div>
              <div style={{
                fontWeight:    700,
                color:         '#111827',
                fontSize:      '14px',
                marginBottom:  '6px',
                letterSpacing: '0.01em',
              }}>
                📧 Notify all active players 30 minutes before this game opens
              </div>
              <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.6 }}>
                Sends a personalised email to all registered players when the game is about to go live.
                Scheduled automatically from the Game Open Time above.
                {config.gameOpenTime && (() => {
                  const openMs = new Date(config.gameOpenTime).getTime();
                  const sendMs = openMs - 30 * 60 * 1000;
                  if (isNaN(sendMs)) return null;
                  const isPast = sendMs < Date.now();
                  return (
                    <span style={{
                      display:    'inline-block',
                      marginTop:  '6px',
                      fontWeight: 600,
                      color:      isPast ? '#F87171' : '#60A5FA',
                    }}>
                      {isPast
                        ? '⚠ Notification time is in the past — email will not send. Set a future Game Open Time.'
                        : `✓ Scheduled for ${new Date(sendMs).toLocaleString('en-AU', {
                            weekday: 'short', day: 'numeric', month: 'short',
                            hour: '2-digit', minute: '2-digit',
                          })}`
                      }
                    </span>
                  );
                })()}
              </div>
            </div>
          </label>
        </div>

        <button type="submit" className="ss-admin-submit-bar-btn" disabled={submitState !== 'idle'}>
          {submitState === 'loading'    ? '⏳ Saving…'
            : submitState === 'validating' ? '🔎 Validating ratings…'
            : isEditMode ? '💾 Save Changes' : '🚀 Publish Game'}
        </button>
      </form>
    </div>
  );
};

export default AdminSetupPage;